import { t } from "i18next";
import { useState } from "react";
import { Trans } from "react-i18next";
import { mutate } from "swr";

import { Button } from "@/components/Button";
import { EMPTY_STRING } from "@/constants";
import { CHAIN_BY_NETWORK, CHAIN_ID } from "@/constants/chains";
import { ColoredNetworkIconMap, NetworkIconMap } from "@/constants/map";
import { useStore } from "@/hooks/store";
import { API_ROUTES } from "@/interfaces/api";
import { Network } from "@/interfaces/network";
import { User } from "@/interfaces/store";
import { useWalletSelector } from "@/providers/near-provider";
import { hapiApi } from "@/services/api/hapi";
import { config } from "@/services/config";
import { CHAIN_INFO } from "@/services/config/evm";
import { near } from "@/services/helpers";
import { cropString } from "@/utils";

import styles from "./styles";
import { SheetTitle } from "../../../styles";

const scanLink = {
  [Network.NEAR]: "https://nearblocks.io",
  [Network.BASE]: CHAIN_INFO[CHAIN_ID.BASE].explorer,
  [Network.LINEA]: CHAIN_INFO[CHAIN_ID.LINEA].explorer,
  [Network.BSC]: CHAIN_INFO[CHAIN_ID.BSC].explorer,
  [Network.TON]: "https://tonscan.org",
  [Network.TON_TESTNET]: "https://testnet.tonscan.org",
  [Network.SOLANA]: "https://solscan.io",
  [Network.ARBITRUM]: "https://arbiscan.io",
  [Network.STELLAR]: "https://stellarchain.io",
  [Network.ETHEREUM]: CHAIN_INFO[CHAIN_ID.ETHEREUM].explorer,
};

const getScanLink = (network: Network, chainId: number | null, user: User) => {
  const explorer = scanLink[network];
  if (network === Network.NEAR && user.nearAccount) return `${explorer}/address/${user.nearAccount}`;
  if (network === Network.STELLAR && user.nearAccount) return `${explorer}/accounts/${user.stellarAccount}`;
  if (network === Network.TON && user.tonAccount) return `${explorer}/address/${user.tonAccount}`;
  if (network === Network.SOLANA && user.solanaAccount) return `${explorer}/account/${user.solanaAccount}`;
  if (chainId && user.evmAccount)
    return explorer + `advanced-filter?fadd=${user.evmAccount}&tadd=${config.evm.attestation[chainId].portalId}`;

  return explorer;
};

export const MinterScore = ({ closeModal }: { closeModal: () => void }) => {
  const { user, level, networks } = useStore((store) => ({
    user: store.user,
    level: store.user.networksData[store.network].level,
    networks: Object.values(store.user.networksData),
  }));

  const { selector } = useWalletSelector();
  const [hash, setHash] = useState<string>(EMPTY_STRING);
  const [loading, setLoading] = useState(false);
  const [currentNetwork, setCurrentNetwork] = useState(Network.NEAR);

  const isNear = currentNetwork === Network.NEAR;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHash(event.target.value);
  };

  const sendHash = async () => {
    setLoading(true);

    try {
      if (isNear) {
        if (!user.nearAccount) return;

        const response = await near.mintScore({
          address: user.nearAccount,
          transaction: hash,
          isUpdateMint: level > 0,
          selector,
        });

        if (response) {
          mutate(`${API_ROUTES.USER}/${user.nearAccount}`);
          closeModal();
        }
      } else {
        const chainId = CHAIN_BY_NETWORK[currentNetwork];
        if (!user.evmAccount || !chainId) return;

        const isUpdateMint = level > 0;
        const action = isUpdateMint ? hapiApi.getUpdateMint : hapiApi.getMint;
        const { data } = await action({ address: user.evmAccount, chainId, transaction: hash });

        if ("hash" in data && data.status === "success") {
          mutate(`${API_ROUTES.USER}/${user.nearAccount}`);
          closeModal();
          setLoading(false);
          return true;
        }
      }
    } catch (error) {
      console.error("Error in sendHash:", error);
    } finally {
      setLoading(false);
    }
  };

  const chainId = CHAIN_BY_NETWORK[currentNetwork];

  const scanLink = getScanLink(currentNetwork, chainId, user);
  const disabledNetwork = [Network.SOLANA, Network.TON];

  return (
    <>
      <SheetTitle>{t("Modals.EnterHash.Title")}</SheetTitle>
      <styles.List>
        <styles.NetworkContainer className="no-scrollbar">
          {networks.map(({ network }) => (
            <styles.Network
              key={network}
              $isActive={currentNetwork === network}
              onClick={() =>
                !disabledNetwork.includes(network) && currentNetwork !== network && setCurrentNetwork(network)
              }
              $disabled={disabledNetwork.includes(network)}
            >
              {currentNetwork === network ? ColoredNetworkIconMap[network] : NetworkIconMap[network]}
              <styles.Text>{network}</styles.Text>
            </styles.Network>
          ))}
        </styles.NetworkContainer>
        <styles.Element>
          1. {t("Modals.EnterHash.Solutions.1")}
          <a href={scanLink} target="_blank">
            {new URL(scanLink).hostname}
          </a>
        </styles.Element>
        {isNear && (
          <styles.Element>
            2. {t("Modals.EnterHash.Solutions.2")}
            <table>
              <tbody>
                <tr>
                  <td>
                    <strong>TXN HASH</strong>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>METHOD</td>
                  <td>TRANSFER</td>
                </tr>
                <tr>
                  <td>FROM</td>
                  <td>{cropString({ query: user.nearAccount || "" })}</td>
                </tr>
                <tr>
                  <td>TO</td>
                  <td>{cropString({ query: config.near.score })}</td>
                </tr>
              </tbody>
            </table>
          </styles.Element>
        )}
        <styles.Element>
          {isNear ? "3" : "2"}. <Trans i18nKey="Modals.EnterHash.Solutions.3" components={{ bold: <strong /> }} />
        </styles.Element>
      </styles.List>
      <styles.StyledInput value={hash} onChange={handleChange} placeholder={t("Modals.EnterHash.Placeholders")} />
      <Button type="black" handler={sendHash} name="Check" disabled={!hash} loading={loading} />
    </>
  );
};
